import { useState, useCallback, useRef, useMemo, useEffect } from "react";

export type UseFormFieldOptions = {
  name: string;
  checks?: ValidateCheck[];
  defaultValue?: string;
  onError?: (errorMessage?: string) => void;
  onSuccess?: (value: string) => void;
};

export default function useFormField(options: UseFormFieldOptions) {
  const { checks = [], defaultValue = "", onError, onSuccess, name } = options;

  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>(defaultValue);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const errors: string[] = useMemo(
    () => validateValue(value, checks),
    [value, checks]
  );

  const errorMessage: string | undefined = useMemo(
    () => (isDirty ? errors?.[0] : undefined),
    [isDirty, errors]
  );

  useEffect(() => {
    if (errorMessage) onError?.(errorMessage);
    else onSuccess?.(value);
  }, [errorMessage, value, onSuccess, onError]);

  const onFocus = useCallback(() => {
    setTimeout(() => setIsDirty(true), 1000);
  }, []);

  const resetFieldState = useCallback(() => {
    setValue("");
    setIsDirty(false);
  }, []);

  const inputProps = useMemo(() => {
    return {
      value,
      onValueChange: setValue,
      onFocus,
      name,
      ref,
      errorMessage,
    } as const;
  }, [value, onFocus, name, ref, errorMessage]);

  return {
    errorMessage,
    isDirty,
    value,
    resetFieldState,
    errors,
    inputProps,
  };
}

export type ValidateCheck = {
  test: (value: string) => boolean;
  message: string;
};

function validateValue(value: string, checks: ValidateCheck[]): string[] {
  const errors: string[] = [];
  checks.forEach(({ test, message }) => {
    if (!test(value)) errors.push(message);
  });

  return errors;
}
